import {
    Box,
    Divider,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    MenuItem,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { CircleMinus } from 'lucide-react';
import { ReactElement } from 'react';
import { FormattedMessage } from 'react-intl';
import SensitiveInfoTypography from '~/components/SensitiveInfoTypography';
import { BlendCandidate } from '../adapters/types';
import BlendBindingMark from './BlendBindingMark';
import { sharesNameWithAnotherSeries } from './tileViewModel';
import ImageMenuRow from './ImageMenuRow';
import MenuCheck from '~/components/Menu/MenuCheck';
import { STATUS_MARK_PX } from '~/components/Menu/menuRowSizes';

/** True when two candidates name the same image bound the same way. A series
 * registered more than once yields one candidate per registration, so a
 * series-UID match alone is not enough to tell them apart. */
function sameCandidate(a: BlendCandidate, b: BlendCandidate): boolean {
    if (a.seriesInstanceUid !== b.seriesInstanceUid) {
        return false;
    }
    if (a.binding.kind !== b.binding.kind) {
        return false;
    }
    return a.binding.kind === 'mapped' && b.binding.kind === 'mapped'
        ? a.binding.registrationId === b.binding.registrationId
        : true;
}

/**
 * How a candidate lies on the row's own, front-and-centre: the approval
 * status of its registration, an unapproved or rejected one listed rather
 * than hidden so a clinician can see the blend before deciding whether to
 * trust it, or the same frame of reference, which needs no registration.
 */
function BindingLead({ binding }: { binding: BlendCandidate['binding'] }) {
    return (
        <>
            <BlendBindingMark binding={binding} sizePx={STATUS_MARK_PX} />
            {binding.kind === 'mapped' ? (
                <Box component="span" sx={visuallyHidden}>
                    <FormattedMessage
                        id="fusion.registrationState"
                        values={{ state: binding.approvalStatus }}
                    />
                </Box>
            ) : null}
        </>
    );
}

/** What tells a candidate's placement apart: its registration, or the same frame. */
function bindingQualifier({ binding, registrationLabel }: BlendCandidate) {
    if (binding.kind !== 'mapped') {
        return <FormattedMessage id="fusion.sameFrame" />;
    }
    return registrationLabel ? (
        <SensitiveInfoTypography>{registrationLabel}</SensitiveInfoTypography>
    ) : undefined;
}

interface FusedImageOptions {
    readonly candidates: ReadonlyArray<BlendCandidate>;
    /** The image currently fused onto the row, if any. */
    readonly current: BlendCandidate | undefined;
    readonly onFuse: (candidate: BlendCandidate) => void;
    readonly onClear: () => void;
    readonly onDone: () => void;
}

/**
 * The images a row may fuse, as console menu rows, each named by its
 * modality and its name (with its series number while names are hidden),
 * and led by how it lies on the row's own: the approval status of the
 * registration that makes it eligible, or the same frame. When the row
 * already has one, the list ends with the way back to none.
 *
 * Returned as an array of rows rather than as a component: a menu manages
 * focus by looking at its direct children, and a component in their place
 * would take the focus meant for its first row and lose it. The caller
 * spreads the rows into its own list, under any choices of its own.
 */
export function fusedImageOptions({
    candidates,
    current,
    onFuse,
    onClear,
    onDone,
}: FusedImageOptions): ReactElement[] {
    const rows = [
        <ListSubheader key="secondary-label">Secondary image</ListSubheader>,
        ...candidates.map((candidate, index) => {
            const isCurrent = current
                ? sameCandidate(current, candidate)
                : false;
            return (
                <ImageMenuRow
                    key={`${candidate.seriesInstanceUid}-${index}`}
                    selected={isCurrent}
                    onClick={() => {
                        onFuse(candidate);
                        onDone();
                    }}
                    mark={<BindingLead binding={candidate.binding} />}
                    modality={candidate.modality}
                    label={candidate.label}
                    seriesNumber={candidate.seriesNumber}
                    sharesName={sharesNameWithAnotherSeries(
                        candidate,
                        candidates
                    )}
                    qualifier={bindingQualifier(candidate)}
                />
            );
        }),
    ];
    if (!current) {
        return rows;
    }
    return [
        ...rows,
        <Divider key="unload-divider" />,
        <MenuItem
            key="unload"
            dense
            onClick={() => {
                onClear();
                onDone();
            }}
        >
            <ListItemIcon>
                <CircleMinus />
            </ListItemIcon>
            <ListItemText>Unload image</ListItemText>
            <MenuCheck selected={false} />
        </MenuItem>,
    ];
}
